/**
 * THE REP WORKLIST — the default landing view for a rep.
 *
 * Presentational. Every decision about WHAT appears and in WHAT ORDER lives in
 * `repWorklist.js` (pure, 176 assertions, verified against all nine real books
 * on production). This file renders that answer and nothing else, so the
 * ranking cannot drift between the model and the screen.
 *
 * ⚠️ PHONE FIRST, GENUINELY. Reps are between schools; the default layout IS
 * the 390px one and the desk layout is the `min-width` query. No `max-width`
 * override anywhere.
 *
 * ⚠️ "NOT TRUNCATED" AND "READABLE" ARE DIFFERENT TESTS. `check-no-truncation`
 * once passed "University Interscholastic League" rendering one letter per
 * line. Long names WRAP on word boundaries with a sane minimum column; nothing
 * is clipped, ellipsised, or allowed to shred into a vertical letter stack.
 * `tools/worklist-probe-390.mjs` measures that in a real viewport.
 */
import { useEffect, useMemo, useState } from 'react'
import { safeText } from '../../../lib/serverText.js'
import { rank, TARGET_ROWS, DAY_PRESETS, presetWindow } from './repWorklist.js'
import { useUnitsTable } from './useUnitsTable.js'
import { valueText, money } from './opportunityValue.js'
import {
  totalScopeLine, unpricedLine, undatedLine, thinLine, TOTAL_COPY,
} from './periodTotal.js'
import { formatDate, formatCountdownFor } from '../../../lib/championships.js'
import WorkFace from '../WorkFace.jsx'
import IndyMemberActions from './IndyMemberActions.jsx'
import { classOf } from '../championFace.js'
import styles from './RepWorklist.module.css'

const NO_FOCUS = Object.freeze([])

const TONE_CLASS = {
  urgent: styles.toneUrgent,
  warn: styles.toneWarn,
  attention: styles.toneAttention,
  calm: styles.toneCalm,
  danger: styles.toneDanger,
  good: styles.toneGood,
}

/* ---- the date pill ------------------------------------------------------- */

/**
 * ⚠️ A PROJECTED DATE MUST NOT LOOK LIKE A SOURCED ONE. Measured on production
 * 2026-10-03:
 * projected dates outnumber sourced ones in seven of the nine books (Ilia is
 * 1,306 projected against 40 sourced). So this is the majority case, and the
 * difference is carried THREE ways — the word, the dashed rule, and the tint —
 * because colour alone fails for a colour-blind rep and a qualifier alone gets
 * skimmed past.
 */
function DatePill({ row }) {
  const v = row.dateVoice
  const cls = [
    styles.datePill,
    v.sourced ? styles.dateSourced : styles.dateProjected,
    v.disputed ? styles.dateDisputed : '',
  ].filter(Boolean).join(' ')
  if (!row.date) {
    return <span className={`${styles.datePill} ${styles.dateNone}`}>No date on record</span>
  }
  return (
    <span className={cls}>
      <span className={styles.dateValue}>{formatDate(row.date)}</span>
      {!v.sourced && <span className={styles.dateQual}>{v.label}</span>}
      {row.days != null && (
        <span className={styles.dateCountdown}>{formatCountdownFor(row.record, row.days)}</span>
      )}
    </span>
  )
}

/* ---- who to call --------------------------------------------------------- */

/**
 * ⭐ THE HEAD COACH IS RARELY THE BUYER — THE ATHLETIC DIRECTOR USUALLY IS.
 * So the role is rendered beside the person, always. A row that names a school
 * but not a person still leaves the rep doing the work.
 *
 * Where there is no contact, the row says so and offers the next step rather
 * than rendering a button that cannot dial. Contact coverage is uneven: Ohio
 * holds ~15,437 contacts and many states hold none (measured 2026-10-03).
 */
function ContactLine({ row }) {
  if (!row.contact) {
    return (
      <p className={styles.contactMissing}>
        <span className={styles.contactLabel}>Who to call</span>
        <span className={styles.contactNone}>
          No contact loaded for this school{row.state ? ` (${row.state})` : ''}.
        </span>
      </p>
    )
  }
  const c = row.contact
  return (
    <p className={styles.contact}>
      <span className={styles.contactLabel}>Who to call</span>
      <span className={styles.contactName}>{c.name}</span>
      {c.role && <span className={styles.contactRole}>{c.role}</span>}
      {c.school && <span className={styles.contactSchool}>{c.school}</span>}
    </p>
  )
}

/* ---- the actions --------------------------------------------------------- */

/**
 * ⚠️ NO INERT ROWS. `actionsFor()` pushes `open` first and unconditionally, so
 * this never renders an empty bar. An anchor action is a real `<a href>` (so a
 * tap dials or composes); everything else is a real `<button onClick>`.
 * `check-clickable` must cover this view.
 */
export const liveActions = (row) => row.actions.filter((a) => !a.disabled)
export const blockedActions = (row) => row.actions.filter((a) => a.disabled)

function actionRenderer(row, onAction) {
  return function render(a) {
    const cls = [styles.action, a.primary ? styles.actionPrimary : '',
      a.disabled ? styles.actionDisabled : ''].filter(Boolean).join(' ')
    if (a.href && !a.disabled) {
      return (
        <a key={a.kind} className={cls} href={a.href}
          onClick={() => onAction?.(a, row)}>
          {a.label}
          {a.role && <span className={styles.actionRole}>{a.role}</span>}
        </a>
      )
    }
    return (
      <button key={a.kind} type="button" className={cls} disabled={Boolean(a.disabled)}
        title={a.why || undefined} onClick={() => onAction?.(a, row)}>
        {a.label}
      </button>
    )
  }
}

/**
 * ⚠️ NO INERT BAR ON THE FACE, AND A LIVE CONTROL MAY CARRY A WARNING THAT IS
 * READ BEFORE IT IS PRESSED.
 *
 * 🔴 `a.why` ON A LIVE ACTION LANDS IN A `title`, WHICH A PHONE NEVER SHOWS —
 * the exact defect that moved the disabled controls into the disclosure, where
 * their reasons became words. So a live action that has something a rep must
 * know FIRST carries `warn` instead, and it is rendered as a sentence beside
 * the button. The trade control uses it for an unsourced date: Brian's rule is
 * that a projected date must not silently authorise a trade, and learning it
 * from the response is one press too late.
 *
 * `note` is the softer sibling — the server's own sentence about a trade that
 * is already pending, which explains a label rather than warning about it.
 */
function Actions({ row, onAction }) {
  const render = actionRenderer(row, onAction)
  return (
    <div className={styles.actions}>
      {liveActions(row).map((a) => (
        (a.warn || a.note)
          ? (
            <span key={a.kind} className={styles.actionWithWarn}>
              {render(a)}
              {a.warn && <span className={styles.actionWarn} role="note">{a.warn}</span>}
              {a.note && <span className={styles.actionNote}>{a.note}</span>}
            </span>
          )
          : render(a)
      ))}
    </div>
  )
}

/**
 * 🔴 THE DISABLED CONTROLS CAME OFF THE FACE, AND THEIR REASONS BECAME
 * READABLE IN THE SAME MOVE.
 *
 * Four greyed buttons stood ~200px tall on EVERY card — the single largest
 * block on a record, spent entirely on things a rep cannot do yet. And the
 * reason each one was disabled lived in a `title` attribute, which a phone
 * never shows at all: a rep tapped a dead button and got nothing back.
 *
 * ⚠️ THIS IS NOT HIDING THEM. They are still rendered, still disabled, still
 * carrying their own `kind`, one tap away inside the card's one disclosure, and
 * now each one states IN WORDS why it cannot fire. The summary counts them, so
 * a collapsed block can never be read as a row with no further options.
 * Strictly more information than the bar it replaces.
 */
function BlockedActions({ row, onAction }) {
  const render = actionRenderer(row, onAction)
  return (
    <div className={styles.blockedList}>
      {blockedActions(row).map((a) => (
        <p key={a.kind} className={styles.blocked}>
          {render(a)}
          {a.why && <span className={styles.blockedWhy}>{a.why}</span>}
        </p>
      ))}
    </div>
  )
}

/* ---- one row ------------------------------------------------------------- */

function Row({ row, onOpen, onAction }) {
  return (
    /* `id` is the anchor the last-hunt sheet's link lands on (`?focus=`). */
    <li className={`${styles.row}${row.focused ? ` ${styles.rowFocused}` : ''}`} id={`wl-${row.id}`}
      data-record-id={row.id} data-focused={row.focused ? 'true' : undefined}>
      {row.focused && (
        <p className={styles.focusNote}>
          {row.beyondLimit
            ? 'You opened this from the last hunt. It ranks below the rows this list shows, so it is added here.'
            : 'You opened this from the last hunt.'}
        </p>
      )}
      {/* The whole heading is the control into the record, so the biggest tap
        * target on the row does the most common thing. */}
      <button type="button" className={styles.rowHead} disabled={Boolean(row.record?.isIndyGroup)} onClick={() => { if (!row.record?.isIndyGroup) onOpen?.(row) }}>
        {/* ⚠️ FULL NAME, WRAPPED, NEVER CLIPPED. */}
        <span className={styles.rowName}>{row.name}</span>
        {row.governingBody && <span className={styles.rowBody}>{row.governingBody}</span>}
      </button>

      {/* ⭐ THE MONEY, ON THE ROW, BECAUSE IT IS WHAT DECIDED THE ORDER.
        *
        * `units × $price` for this sport and level — NOT the TAM ceiling, which
        * is a market-sizing figure a rep would never say to an athletic
        * director. The range is shown where the sample supports one and the
        * median is named: Brian's worked example for HS football is the RANGE,
        * and the table's median for that row sits inside it. Both are asserted
        * in verify-revenue-rank.mjs §2 rather than quoted here.
        *
        * ⚠️ AND WHERE THERE IS NO FIGURE, THE REASON IS RENDERED — in the
        * endpoint's OWN words. 734 records sit on unmeasured sport+level rows:
        * a visible population, not an edge case. A blank where a dollar figure
        * should be teaches a rep the championship is worthless; the sentence
        * teaches them it is a gap in what we have measured. Those are different
        * things and only one of them is true. */}
      {row.value && (
        row.value.usd != null ? (
          <p className={styles.rowValue} data-confidence={row.value.confidence.key}>
            <span className={styles.rowValueFigure}>{valueText(row.value)}</span>
            {row.value.low != null && row.value.high != null && (
              <span className={styles.rowValueMid}>midpoint {money(row.value.usd)}</span>
            )}
            <span className={styles.rowValueBasis}>
              {row.value.basis} · {row.value.confidence.label}
            </span>
          </p>
        ) : (
          /* ⚠️ A SHORT MARKER ON THE FACE, THE FULL SENTENCE ONE TAP AWAY.
           * The three-line explanation used to sit at the same weight as the
           * dollar figure on every unmeasured card — and 734 records are
           * unmeasured, so for a rep with those it WAS the card. The marker
           * still says the honest thing in six words: it is a measurement gap,
           * not a worthless championship. The server's full sentence is in the
           * disclosure below, verbatim and unedited. */
          <p className={styles.rowValueGap}>
            <span className={styles.rowValueLabel}>No figure — not measured yet</span>
          </p>
        )
      )}

      <div className={styles.rowMeta}>
        <DatePill row={row} />
        {row.sport && <span className={styles.chip}>{row.sport}</span>}
        {row.level && <span className={styles.chip}>{row.level}</span>}
        {classOf(row.record) && <span className={styles.chip}>{classOf(row.record)}</span>}
        {row.state && <span className={styles.chip}>{row.state}</span>}
      </div>

      {/* ⭐ THE CHAMPION STAYS ON THE FACE OF THE CARD — it is the revenue
        * moment, which makes it work rather than support. Its LABEL is gone:
        * a crown glyph and a name read fine, and the ALL-CAPS header was part
        * of what made the card feel like a form. */}
      {/* ⭐ WHO WON, WHO BUYS, WHY IT IS HERE - the shared face (2026-10-26).
        * Brian: "work list doesn't include who the champion is either." The
        * champion line alone named the stored value; a team title now reads as
        * a TEAM row, an individual title (or the one indy-ring item) lists every
        * athlete with their school, the ACCOUNT is named, and the reason is on
        * the card, not only above the group. Same component as the Command
        * Center Work queue, so the two cannot disagree. */}
      <WorkFace record={row.record} reason={row.reason} showWhat={false} />
      {!row.champion.blank && row.champion.note && (
        <p className={styles.championNote}>{row.champion.note}</p>
      )}

      {/* ⭐ THE SPLIT, ON THE FACE OF THE CARD. Brian, 2026-10-26: every team
        * champion gets its own place, every individual winner of a
        * championship goes under ONE Individual (indy ring) grouping, and a
        * split is never a badge with nothing behind it. So the reason is on
        * the card (not behind the disclosure), the indy ring names every
        * athlete with their school, and the primary action above says what to
        * do. Wrapped, never clipped — the standing rule on this board. */}
      {row.splitAction && (
        <p className={styles.splitReason} data-role={row.family?.role || ''}>
          {row.splitAction.reason}
        </p>
      )}
      {row.family?.role === 'team' && row.family.parentName && (
        <p className={styles.splitParent}>Team title of {row.family.parentName}</p>
      )}

      {/* 🔴 EVERYTHING BELOW HERE IS SUPPORT, AND IT NOW SITS BEHIND ONE TAP.
        *
        * Brian on 2.96.0: *"User interface is still terrible."* The card carried
        * seven labelled sections at similar visual weight — champion, who to
        * call, why it is yours, the derivation note, the no-figure explanation,
        * the meta row, the actions — so a rep had to read a form to find the
        * one thing they do next. Thirty of them ran past 12,000px.
        *
        * ⚠️ NOTHING IS DELETED, AND THAT IS THE WHOLE DESIGN. Every reason,
        * denominator and derivation is still here, one tap away, in the same
        * words. A `<details>` is honest in a way that hiding is not: the marker
        * states what is inside and the content is keyboard-reachable, in the
        * DOM, and findable by search. What changed is what COMPETES for the
        * eye — which is the actual complaint.
        *
        * ⚠️ AND THE SUMMARY NAMES WHAT IS IN IT rather than saying "Details".
        * A rep who cannot tell whether a disclosure holds a phone number or a
        * footnote will not open it, and then the honesty may as well be gone. */}
      {row.record?.isIndyGroup
        ? <IndyMemberActions summary={row.record} onOpen={onOpen} onAction={onAction} />
        : <Actions row={row} onAction={onAction} />}

      <details className={styles.more}>
        <summary className={styles.moreSummary}>
          {row.contact
            ? `Who to call · ${row.contact.name}`
            : 'No contact loaded'}
          {/* ⚠️ ONE DISCLOSURE PER CARD, NOT TWO. Two summary rows cost 96px of
            * affordance on every record and asked a rep to guess which one held
            * the thing they wanted. The summary names everything inside it. */}
          <span className={styles.moreRest}> · why it is yours{
            row.value && row.value.usd == null ? ' · why there is no figure' : ''
          }{
            blockedActions(row).length > 0
              ? ` · ${blockedActions(row).length} more action${blockedActions(row).length === 1 ? '' : 's'}`
              : ''
          }</span>
        </summary>

        <div className={styles.moreBody}>
          <ContactLine row={row} />

          {/* The full no-figure sentence lives here; the card face carries the
            * short marker. The server's words, unchanged. */}
          {row.value && row.value.usd == null && row.value.reason && (
            <p className={styles.moreText}>{safeText(row.value.reason)}</p>
          )}

          {/* WHY IT IS THEIRS. Computed from the governing-body rule, because
            * `sr_owner_assign_reason` is writable but non-blank on 0 of 15,111
            * records (measured 2026-10-03) — so reading the field would print a
            * blank on every row. */}
          <p className={styles.moreText}>
            {row.ownership.why}
            {!row.ownership.sourced && row.ownership.assigned && (
              <span className={styles.whyComputed}>
                {' '}— derived from the assignment rule, not stored on the record
              </span>
            )}
          </p>

          {blockedActions(row).length > 0 && (
            <BlockedActions row={row} onAction={onAction} />
          )}
        </div>
      </details>
    </li>
  )
}

/* ---- the view ------------------------------------------------------------ */

export default function RepWorklist({
  book = [],
  status = 'ready',
  error = null,
  scopeMode = 'own',
  contacts = {},
  composedReasons = {},
  distributeTo = [],
  /* ⚠️ THE TRADE SERVICE'S ANSWER, PASSED IN. The view fetches nothing; the
   * container owns the call and this is read-only here, exactly like
   * `contacts`. Keyed by record id, with `'*'` for the answer true of every
   * row. An empty object means nothing has been told yet, and the control says
   * so rather than guessing in either direction. */
  tradeStates = {},
  /* The whole loaded object, read-only, so split families are recognised
   * across books (see repWorklist.rank `universe`). */
  universe = null,
  /* Record ids a link asked to open; see rank(). A shared empty array so an
   * absent prop does not re-run the ranking on every render. */
  focusIds = NO_FOCUS,
  limit = TARGET_ROWS,
  repName = '',
  onOpen,
  onAction,
  onRetry,
}) {
  /* 🔴 PRIOR SEASONS ARE OFF BY DEFAULT. Brian, looking at the live view:
   * *"it brings up stale opportunities from 2024. Those are not relevant and are
   * completely spoken for."* Default OFF is the fix; the toggle is what keeps
   * them reachable, because nothing on this board may become invisible. */
  const [showPrior, setShowPrior] = useState(false)

  /* 🔴 THE DATE IS A DIMENSION OF THE VIEW, AND IT STARTS OFF.
   * Brian: *"A rep must be able to work A SPECIFIC DAY — what do I do today,
   * what's next week."* `all` is the default so the screen a rep opens is
   * unchanged; the date is a lens they reach for, not a scope imposed on them. */
  const [dayPreset, setDayPreset] = useState('all')
  /* ⭐ THE CUSTOM PERIOD. `YYYY-MM-DD` strings straight off a date input, parsed
   * into LOCAL dates — `new Date('2026-10-05')` parses as UTC midnight, which
   * west of Greenwich is the previous day, and a period that is silently a day
   * out is the worst kind of wrong here. */
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  /* ⭐ THE MONEY. One cacheable call over sport × level, shared across the page,
   * which is what makes ordering the whole assignment by dollars possible. */
  const units = useUnitsTable()
  const [orderBy, setOrderBy] = useState('revenue')

  const result = useMemo(
    () => {
      const localDay = (s) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '')
        return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
      }
      const { from, to } = dayPreset === 'custom'
        ? { from: localDay(customFrom), to: localDay(customTo) }
        : presetWindow(dayPreset)
      return rank(book, {
        limit, contacts, composedReasons, distributeTo, status, error, scopeMode,
        includePriorSeasons: showPrior, dateFrom: from, dateTo: to,
        unitsTable: units.table, orderBy, tradeStates, universe, focusIds,
      })
    },
    [book, limit, contacts, composedReasons, distributeTo, status, error, scopeMode,
      showPrior, dayPreset, customFrom, customTo, units.table, orderBy, tradeStates, universe, focusIds],
  )

  /* Land ON the linked row once it has rendered, not at the top of the list. */
  const focusKey = (focusIds || []).join(',')
  const focusRendered = result.rows.some((r) => r.focused)
  useEffect(() => {
    if (!focusKey || !focusRendered) return
    const el = document.getElementById(`wl-${focusKey.split(',')[0]}`)
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' })
  }, [focusKey, focusRendered])

  /* ⚠️ NEVER A BARE ZERO. Five distinct states with five distinct sentences:
   * loading, load-failed, not-mapped-to-a-book, book-being-built, clear day.
   * A non-exec was once silently scoped to an orders-derived book where every
   * count collapsed to 0 while an exec saw the truth. */
  if (result.empty) {
    const copy = result.emptyCopy
    return (
      <section className={styles.wrap} aria-label="Your worklist">
        <div className={`${styles.emptyCard} ${TONE_CLASS[copy.tone] || ''}`}>
          <h2 className={styles.emptyHead}>{copy.headline}</h2>
          <p className={styles.emptyBody}>{copy.body}</p>
          <div className={styles.actions}>
            {copy.actions?.map((a) => (
              <button key={a.kind} type="button" className={styles.action}
                onClick={() => onAction?.(a, null)}>{a.label}</button>
            ))}
            {(result.empty === 'failed') && (
              <button type="button" className={`${styles.action} ${styles.actionPrimary}`}
                onClick={() => onRetry?.()}>Try again</button>
            )}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.wrap} aria-label="Your worklist">
      <header className={styles.head}>
        <h2 className={styles.title}>What to act on{repName ? `, ${repName}` : ''}</h2>
        <p className={styles.sub}>
          {result.rows.length} of {result.bookSize.toLocaleString()} championships,
          ranked by what needs you first.
        </p>
      </header>

      {/* 🔴 THE SEASON SCOPE, STATED, WITH THE WAY BACK.
        *
        * Brian, on the live view: *"it brings up stale opportunities from 2024.
        * Those are not relevant and are completely spoken for."* Those records
        * were not merely present — `tierOf` reads crowned-and-unworked as tier
        * 1, "the revenue moment", so a 2024 championship with a champion and no
        * logged outreach was the FIRST thing a rep saw.
        *
        * ⚠️ THE SCOPE IS ANNOUNCED WHETHER OR NOT ANYTHING WAS EXCLUDED. A view
        * that silently narrows is the same class of problem as one that silently
        * truncates: the rep cannot tell a quiet week from a filtered one. So the
        * current season is always named, and the count and the seasons behind
        * the control are named when there are any. */}
      {/* 🔴 THE ORDER, AND IT DECLARES ITSELF.
        *
        * Brian: *"Revenue is not a sort option bolted onto urgency tiers — it is
        * how a rep decides what to do today… A big football championship
        * crowning in three weeks outranks a small title crowning tomorrow, and
        * the view should say so."* So dollars lead and the tier is the tie-break,
        * across the whole in-scope set rather than inside each tier.
        *
        * ⚠️ WHEN THE UNITS TABLE IS NOT THERE, THE LIST SAYS SO RATHER THAN
        * CLAIMING A RANKING IT COULD NOT COMPUTE. `ordering.mode` is what the
        * model actually did, not what was asked for. */}
      <div className={styles.orderBar} role="group" aria-label="What decides the order">
        {[['revenue', 'By revenue'], ['urgency', 'By urgency']].map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={styles.dayChip}
            data-on={result.ordering.mode === k}
            aria-pressed={result.ordering.mode === k}
            disabled={k === 'revenue' && !result.ordering.revenueAvailable}
            title={k === 'revenue' && !result.ordering.revenueAvailable
              ? result.ordering.whyNotRevenue
              : undefined}
            onClick={() => setOrderBy(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 🔴 THE DAY LENS. "What do I do today" is one tap, not a date picker a
        * rep has to operate on a phone. */}
      <div className={styles.dayBar} role="group" aria-label="Scope the work to a date">
        {DAY_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={styles.dayChip}
            data-on={dayPreset === p.key}
            aria-pressed={dayPreset === p.key}
            onClick={() => setDayPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ⭐ CUSTOM DATES — the period a preset cannot name.
        * Brian asked to "sort by a date period", and a fixed set of day-counts
        * is not a period selector. Rendered only when `custom` is the active
        * preset so the common case stays one tap. */}
      {dayPreset === 'custom' && (
        <div className={styles.customRange}>
          <label className={styles.rangeField}>
            <span>From</span>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </label>
          <label className={styles.rangeField}>
            <span>To</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </label>
          {!customFrom && !customTo && (
            <p className={styles.rangeHint}>
              Pick at least one date. Until you do, every date is in scope — nothing is hidden.
            </p>
          )}
        </div>
      )}

      {/* ⭐ THE PERIOD SUM. Brian: *"I want to be able to have my team sort by a
        * date period for sum."*
        *
        * 🔴 IT TOTALS THE PERIOD, NOT THE THIRTY ROWS ON SCREEN, and it says
        * which population it covered on its own face — a sum of thirty looks
        * exactly like a sum of eight hundred, so the count is not optional.
        *
        * ⚠️ THE MIDPOINT IS THE PROMINENT FIGURE. The ceiling is every record
        * simultaneously at its largest observed size, which will not happen,
        * and it is worded that way here so it cannot be quoted as a forecast. */}
      {result.total.records > 0 && (
        <section className={styles.periodSum} aria-label="What this period is worth">
          <p className={styles.sumScope}>
            {totalScopeLine(result.total, {
              who: repName,
              periodLabel: DAY_PRESETS.find((p) => p.key === dayPreset)?.label || '',
              dated: result.dateWindow.on,
            })}
          </p>

          {result.total.hasFigure ? (
            <>
              <p className={styles.sumMid}>
                <span className={styles.sumFigure}>{money(result.total.mid)}</span>
                <span className={styles.sumMidLabel}>{TOTAL_COPY.mid}</span>
              </p>
              <p className={styles.sumRange}>
                <span><b>{money(result.total.floor)}</b> {TOTAL_COPY.floor}</span>
                <span><b>{money(result.total.ceiling)}</b> {TOTAL_COPY.ceiling}</span>
              </p>
              <p className={styles.sumCounts}>
                <b>{result.total.priced.toLocaleString()}</b> of{' '}
                <b>{result.total.records.toLocaleString()}</b> priced
              </p>
            </>
          ) : (
            /* ⚠️ NEVER A BARE $0. A period whose records are all unmeasured has
             * no figure, which is a different thing from being worth nothing. */
            <p className={styles.sumNone}>
              No figure for this period — none of its {result.total.records.toLocaleString()}{' '}
              records sits on a measured sport and level yet.
            </p>
          )}

          {unpricedLine(result.total) && (
            <p className={styles.sumNote}>{unpricedLine(result.total)}</p>
          )}
          {thinLine(result.total) && (
            <p className={styles.sumNote}>{thinLine(result.total)}</p>
          )}
          {/* 🔴 UNDATED NEVER FOLDS INTO "OUTSIDE THIS PERIOD". */}
          {result.dateWindow.on && undatedLine(result.dateWindow.undated, {
            total: result.bookSize,
          }) && (
            <p className={styles.sumNote}>
              {undatedLine(result.dateWindow.undated, { total: result.bookSize })}
            </p>
          )}
          {/* ⚠️ THE TWO RULES THAT COLLIDE, SAID ON SCREEN. A rep who sees
            * 2025-2026 records with no explanation reasonably concludes the
            * stale-record fix broke. */}
          {result.dateWindow.reachesPriorSeason && (
            <p className={styles.sumNote}>
              This window reaches back before the season started, so it crosses{' '}
              {result.dateWindow.spansSeasons.join(' and ')}. Those earlier records are
              normally held out of the tiers as concluded — you asked for the period, so
              they are counted here.
            </p>
          )}
          {/* 🔴 NEVER IMPLY NEGLECT WE CANNOT EVIDENCE. */}
          {result.crowned.inPeriod > 0 && (
            <p className={styles.sumNote}>
              <b>{result.crowned.inPeriod.toLocaleString()}</b> of these are crowned
              {result.crowned.statusUntouched > 0 && (
                <> — <b>{result.crowned.statusUntouched.toLocaleString()}</b> with no pursuit
                  status set</>
              )}. {result.crowned.why}
            </p>
          )}
        </section>
      )}

      {/* 🔴 ONE STRIP, NOT FOUR. Measured on the harness at a true 390px, the
        * blocks between the header and the first record came to 304px — an
        * order explanation (98), the date chips (94), the season line (44) and
        * the caveat summary (68). A rep scrolled past all of it every single
        * time to reach the thing they came for.
        *
        * The ordering explanation, the data caveats and the units retry are now
        * ONE disclosure with ONE 44px summary line that carries the ordering in
        * words and the caveat COUNT. Nothing is deleted and nothing is
        * reworded — the same sentences, one tap away, in the same order.
        *
        * ⚠️ IT OPENS BY DEFAULT WHEN REVENUE IS UNAVAILABLE, because "this is
        * NOT ranked by dollars" is not support — it is a correction to what the
        * chip above appears to claim, and a rep who does not read it has been
        * misled by a control, not merely under-informed. */}
      <details className={styles.about} open={!result.ordering.revenueAvailable}>
        <summary className={styles.aboutSummary}>
          {result.ordering.mode === 'revenue'
            ? 'Ranked by dollars'
            : 'Ranked by urgency, NOT dollars'}
          {result.caveats.length > 0 && (
            <span className={styles.caveatHint}>
              {' '}· {result.caveats.length} thing{result.caveats.length === 1 ? '' : 's'} to know
            </span>
          )}
          {result.ordering.unmodelledRows > 0 && (
            <span className={styles.caveatHint}>
              {' '}· {result.ordering.unmodelledRows.toLocaleString()} without a figure
            </span>
          )}
        </summary>
        <div className={styles.aboutBody}>
        <span className={styles.orderNote}>
          {result.ordering.mode === 'revenue'
            ? <>Ordered by dollars, most valuable first — urgency breaks a tie.</>
            : <>Ordered by urgency.{' '}
              {result.ordering.requested === 'revenue' && !result.ordering.revenueAvailable && (
                /* ⚠️ THE SENTENCE COMES FROM THE MODEL, NOT FROM HERE. It said
                 * "the units table did not load" for BOTH failures, so a table
                 * that loaded fine and simply carries no price reported itself
                 * as a failed fetch — above cards correctly saying the
                 * opposite. Those two states have different owners, so the view
                 * renders whichever actually happened. */
                <b>This is NOT revenue-ranked. {result.ordering.whyNotRevenue}</b>
              )}
            </>}
          {result.ordering.unmodelledRows > 0 && (
            <>
              {' '}
              <b>{result.ordering.unmodelledRows.toLocaleString()}</b> of{' '}
              {(result.ordering.unmodelledRows + result.ordering.modelledRows).toLocaleString()} in
              scope have no measured units for their sport and level, so they carry no figure — each
              one says why on its row.
            </>
          )}
          {units.status === 'error' && (
            <>
              {' '}
              <button type="button" className={styles.seasonToggle} onClick={units.reload}>
                Retry the units table
              </button>
            </>
          )}
        </span>

        {/* ⚠️ EVERY CAVEAT, IN FULL, IN THE SAME WORDS. The count is on the
          * summary line so a collapsed strip can never be mistaken for a clean
          * book; the sentences themselves are here, unedited. */}
        {result.caveats.length > 0 && (
          <ul className={styles.caveats}>
            {result.caveats.map((c, i) => (
              <li key={i} className={styles.caveat}>{c.text}</li>
            ))}
          </ul>
        )}

        {/* ⚠️ THE JUL-TO-JUN CONVENTION AND THE EXCLUDED SEASONS. The face
          * states WHICH season and HOW MANY are excluded; this is why. */}
        <span className={styles.orderNote}>
          SR files a season Jul to Jun.
          {result.season.priorSeasonCount > 0 && (
            <>
              {' '}The excluded records are from{' '}
              {result.season.priorSeasonLabels.slice(0, 4).join(', ')}
              {result.season.priorSeasonLabels.length > 4
                ? ` and ${result.season.priorSeasonLabels.length - 4} earlier season(s)`
                : ''}
              {' '}— reachable, not hidden.
            </>
          )}
        </span>
        </div>
      </details>

      {/* ⚠️ A FILTERED DAY MUST NOT READ AS A CLEAR DAY. All three populations
        * are stated — in the window, outside it, and the ones that carry NO
        * date and therefore sit on no day at all. That last number is most of
        * some reps' work, and without it an empty "Today" is indistinguishable
        * from a quiet Tuesday. */}
      {result.dateWindow.on && (
        <p className={styles.dayScope} role="status">
          Showing <b>{result.dateWindow.inWindow.toLocaleString()}</b>
          {' '}dated {result.dateWindow.from === result.dateWindow.to
            ? <>on <b>{result.dateWindow.from}</b></>
            : <>between <b>{result.dateWindow.from}</b> and <b>{result.dateWindow.to}</b></>}.
          {result.dateWindow.outOfWindow > 0 && (
            <> {result.dateWindow.outOfWindow.toLocaleString()} fall outside it.</>
          )}
          {result.dateWindow.undated > 0 && (
            <>
              {' '}
              <b>{result.dateWindow.undated.toLocaleString()}</b> carry no date at all, so they sit
              on no day and are not in this view — that is a gap in the records, not a quiet day.
            </>
          )}
          {result.dateWindow.inWindow === 0 && (
            <> <b>Nothing is dated in this window.</b></>
          )}
        </p>
      )}

      {/* ⚠️ THE SEASON LINE STAYS ON THE FACE, SHORT. Which season a rep is
        * looking at is scope, not a footnote — a view that silently narrowed
        * would be the defect this fixed in the first place. What moved behind
        * the control is the EXPLANATION of the Jul-to-Jun convention and the
        * list of excluded seasons, which a rep reads once. */}
      <p className={styles.seasonScope}>
        {result.season.showingPriorSeasons
          ? <>Showing <b>every season</b>, including concluded ones.</>
          : <><b>{result.season.currentLabel} season</b></>}
        {result.season.priorSeasonCount > 0 && (
          <>
            {' · '}
            <b>{result.season.priorSeasonCount.toLocaleString()}</b> concluded, excluded
          </>
        )}
        {(result.season.priorSeasonCount > 0 || result.season.showingPriorSeasons) && (
          <button
            type="button"
            className={styles.seasonToggle}
            onClick={() => setShowPrior((v) => !v)}
          >
            {result.season.showingPriorSeasons
              ? `Back to ${result.season.currentLabel} only`
              : 'Show concluded'}
          </button>
        )}
      </p>

      {/* ⚠️ IF THE LIST IS NOT ACTUALLY RANKED, SAY SO. Dormant on production
        * today (every book carries dates) but it is the guard against showing
        * an alphabetical list as though it were ordered by urgency. */}
      {!result.prioritised && (
        <p className={`${styles.notice} ${styles.toneWarn}`}>{result.unprioritisedReason}</p>
      )}

      {result.groups.map((g) => (
        <div key={g.key} className={styles.group}>
          <div className={`${styles.groupHead} ${TONE_CLASS[g.tone] || ''}`}>
            <h3 className={styles.groupTitle}>{g.label}</h3>
            <span className={styles.groupCount}>
              {g.rows.length === g.total
                ? `${g.total.toLocaleString()}`
                : `${g.rows.length} of ${g.total.toLocaleString()}`}
            </span>
          </div>
          <p className={styles.groupWhy}>{g.why}</p>
          <ul className={styles.rows}>
            {g.rows.map((row) => (
              <Row key={row.id} row={row} onOpen={onOpen} onAction={onAction} />
            ))}
          </ul>
        </div>
      ))}

      {/* PAGED, NEVER CAPPED — the standing rule on this board. */}
      {result.held > 0 && (
        <p className={styles.held}>
          {result.held.toLocaleString()} more in your book, reachable and not hidden.
          This view is deliberately short: it is what to act on now, not the whole book.
        </p>
      )}
    </section>
  )
}
